import { useState, useMemo } from 'react';
import { Empty, RadioChangeEvent } from 'antd';
import { RoleEnum } from '@/types/generated/graphql';
import { useTeams } from '@modules/teams/hooks/use-submenu-teams';
import { FilterRecentChatMembersProps } from '../members.types';
import { RiSearchLine, RiTeamLine, RiUserSettingsLine } from 'react-icons/ri';
import { AppFilter } from '@components/app-filter';
import { DebounceInput } from '@components/form/input/debounce-input';
import { useTranslation } from 'react-i18next';
import { capitalize, capitalizeFirst } from '@shared/utils/capitalize';

export function useMemberFilters(setFilter: FilterRecentChatMembersProps['setFilter']) {
  const [selectedRole, setSelectedRole] = useState<RoleEnum | null>(null);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const { data, loadNextRef, hasNextPage, filter, setFilter: setTeamsFilter } = useTeams();
  const [isFilterOpen, setFilterOpen] = useState(false);
  const { t } = useTranslation();

  const handleRoleChange = (e: RadioChangeEvent) => {
    const roleValue = e?.target?.value;
    setSelectedRole(roleValue);

    setFilter((prev) => ({
      ...prev,
      role: roleValue,
    }));
  };

  const handleCheckTeams = (teams: string[]) => {
    setSelectedTeams(teams);
    setFilter((prev) => ({
      ...prev,
      teams: teams.length > 0 ? teams : null,
    }));
  };

  const teamsName = useMemo(() => {
    return data?.data?.map((team) => ({
      label: team?.name,
      value: team?.id,
    }));
  }, [data]);

  const filters = [
    {
      label: capitalize(t('role', { count: 1 })),
      icon: <RiUserSettingsLine size={20} />,
      children: (
        <AppFilter.Radio
          value={selectedRole}
          onChange={handleRoleChange}
          className="w-64!"
          options={[
            { label: capitalize(t('owner')), value: RoleEnum.owner },
            { label: capitalize(t('admin', { count: 1 })), value: RoleEnum.admin },
            { label: capitalize(t('member', { count: 1 })), value: RoleEnum.member },
          ]}
        />
      ),
    },
    {
      label: capitalize(t('team', { count: 1 })),
      icon: <RiTeamLine size={20} />,
      header: (
        <DebounceInput
          allowClear
          size="large"
          prefix={<RiSearchLine />}
          placeholder={capitalizeFirst(t('find_team')) + '..'}
          defaultValue={filter.query}
          setDebouncedValue={(v) => setTeamsFilter((prev) => ({ ...prev, query: v }))}
        />
      ),
      children: (
        <>
          {teamsName && teamsName?.length > 0 ? (
            <AppFilter.Checkbox
              value={selectedTeams}
              hasNextPage={hasNextPage}
              loadNextRef={loadNextRef}
              onChange={handleCheckTeams}
              className="w-64!"
              options={teamsName}
            />
          ) : (
            <Empty className="py-11!" description={capitalizeFirst(t('no_teams_found')) + '...'} />
          )}
        </>
      ),
    },
  ];

  return {
    selectedRole,
    selectedTeams,
    teamsName,
    filter,
    hasNextPage,
    loadNextRef,
    setTeamsFilter,
    handleRoleChange,
    handleCheckTeams,
    isFilterOpen,
    setFilterOpen,
    filters,
  };
}
